import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost, apiPut } from "../api/client";

// ── Telegram ──────────────────────────────────────────────────────────────────

interface TelegramStatus {
  bound: boolean;
  configured: boolean;
}

interface TelegramBindToken {
  token: string;
  expiresAt: string;
  deepLink: string;
}

export function useTelegramStatus() {
  return useQuery<TelegramStatus, Error>({
    queryKey: ["telegram-status"],
    queryFn: () => apiGet<TelegramStatus>("/api/me/telegram/status"),
    staleTime: 30_000,
  });
}

export function useTelegramBind() {
  const qc = useQueryClient();
  return useMutation<TelegramBindToken, Error, void>({
    mutationFn: () => apiPost<TelegramBindToken>("/api/me/telegram/bind-token", {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
}

// ── Teams webhook ─────────────────────────────────────────────────────────────

interface TeamsWebhook {
  webhook_url: string | null;
}

export function useTeamsWebhook(workspaceId: string) {
  return useQuery<TeamsWebhook, Error>({
    queryKey: ["teams-webhook", workspaceId],
    enabled: Boolean(workspaceId),
    queryFn: () => apiGet<TeamsWebhook>(`/api/workspaces/${workspaceId}/teams-webhook`),
    staleTime: 60_000,
  });
}

export function useUpdateTeamsWebhook(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation<{ ok: boolean }, Error, string>({
    mutationFn: (webhook_url: string) =>
      apiPut<{ ok: boolean }>(`/api/workspaces/${workspaceId}/teams-webhook`, { webhook_url }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["teams-webhook", workspaceId] });
    },
  });
}
